import { useAtomValue } from 'jotai'
import { useTar } from '@/modules'
import { ToggleGroup, ToggleGroupItem } from '@duck/ui'
import { useTheme, type ThemeName } from '@/contexts/ThemeContext'
import { useLocalSettings } from '@/hooks/useLocalSettings'
import { DEFAULT_TAB_STYLE, type TabStyle } from '@/components/WorkspaceTabs/tabStyle'
import { WorkspaceTabsView } from '@/components/WorkspaceTabs/WorkspaceTabsView'
import { ChallengeService } from '@duck/api-definitions'
import { FONT_SIZE_NORMAL, FONT_SIZE_PRESETS, type FontSizePreset } from '@/hooks/useFontSize'
import { trackWorkspaceSettings } from '@/modules/workspaceSettingsAnalytics'
import { workspaceChallengeAtom } from '@/store/workspaceAtoms'
import { SettingRow } from '../SettingRow'

const PREVIEW_TABS_STATE = {
  tabs: [
    { type: ChallengeService.WorkspaceTabTypeEnum.Assignment },
    { type: ChallengeService.WorkspaceTabTypeEnum.Sql },
    { type: ChallengeService.WorkspaceTabTypeEnum.Notes },
  ],
  activeTabIndex: 0,
}

const noop = () => {}

const numberToPreset = (size: number): FontSizePreset => {
  const match = (Object.entries(FONT_SIZE_PRESETS) as [FontSizePreset, number][]).find(
    ([, value]) => value === size
  )
  return match?.[0] ?? 'normal'
}

interface AppearancePanelProps {
  onChange?: () => void
}

export const AppearancePanel = ({ onChange }: AppearancePanelProps) => {
  const { t } = useTar()
  const { themeName, setTheme } = useTheme()
  const { getSetting, setSetting } = useLocalSettings()
  const challenge = useAtomValue(workspaceChallengeAtom)

  const fontSize = getSetting('fontSize') ?? FONT_SIZE_NORMAL
  const tabStyle = getSetting('tabStyle') ?? DEFAULT_TAB_STYLE
  const fontSizePreset = numberToPreset(fontSize)

  return (
    <div>
      <div className="mb-1 text-lg font-semibold text-foreground">
        {t('common.settingsAppearance')}
      </div>
      <div className="mb-5 text-xs text-muted-foreground">
        {t('common.settingsAppliedAcrossAllChallenges')}
      </div>

      <SettingRow
        label={t('common.settingsFontSize')}
        control={
          <ToggleGroup
            type="single"
            value={fontSizePreset}
            onValueChange={(v) => {
              if (!v || v === fontSizePreset) return
              const nextPreset = v as FontSizePreset
              const nextValue = FONT_SIZE_PRESETS[nextPreset]
              setSetting('fontSize', nextValue)
              trackWorkspaceSettings('workspace_settings:appearance:font_size_change', {
                challengeId: challenge?.id,
                from: fontSizePreset,
                to: nextPreset,
                fromValue: fontSize,
                toValue: nextValue,
              })
              onChange?.()
            }}
            className="rounded-md border border-border bg-muted/30 p-0.5"
          >
            <ToggleGroupItem value="small" size="sm">
              {t('common.settingsFontSizeSmall')}
            </ToggleGroupItem>
            <ToggleGroupItem value="normal" size="sm">
              {t('common.settingsFontSizeNormal')}
            </ToggleGroupItem>
            <ToggleGroupItem value="large" size="sm">
              {t('common.settingsFontSizeLarge')}
            </ToggleGroupItem>
          </ToggleGroup>
        }
      />

      <SettingRow
        label={t('common.settingsTheme')}
        control={
          <ToggleGroup
            type="single"
            value={themeName}
            onValueChange={(v) => {
              if (!v || v === themeName) return
              const next = v as ThemeName
              setTheme(next)
              trackWorkspaceSettings('workspace_settings:appearance:theme_change', {
                challengeId: challenge?.id,
                from: themeName,
                to: next,
              })
              onChange?.()
            }}
            className="rounded-md border border-border bg-muted/30 p-0.5"
          >
            <ToggleGroupItem value="light" size="sm">{t('themes.light')}</ToggleGroupItem>
            <ToggleGroupItem value="dark" size="sm">{t('themes.dark')}</ToggleGroupItem>
            <ToggleGroupItem value="system" size="sm">{t('themes.system')}</ToggleGroupItem>
          </ToggleGroup>
        }
      />

      <SettingRow
        label={t('common.settingsTabStyle')}
        control={
          <ToggleGroup
            type="single"
            value={tabStyle}
            onValueChange={(v) => {
              if (!v || v === tabStyle) return
              const next = v as TabStyle
              setSetting('tabStyle', next)
              trackWorkspaceSettings('workspace_settings:appearance:tab_style_change', {
                challengeId: challenge?.id,
                from: tabStyle,
                to: next,
              })
              onChange?.()
            }}
            className="rounded-md border border-border bg-muted/30 p-0.5"
          >
            <ToggleGroupItem value="rounded" size="sm">
              {t('common.settingsTabStyleRounded')}
            </ToggleGroupItem>
            <ToggleGroupItem value="boxes" size="sm">
              {t('common.settingsTabStyleBoxes')}
            </ToggleGroupItem>
            <ToggleGroupItem value="underline" size="sm">
              {t('common.settingsTabStyleUnderline')}
            </ToggleGroupItem>
          </ToggleGroup>
        }
      />

      <div className="mt-3 h-[160px] rounded-md border border-border overflow-hidden bg-background">
        <WorkspaceTabsView
          previewMode
          initialTabsState={PREVIEW_TABS_STATE}
          onTabsChange={noop}
          onTabsDataChange={noop}
        />
      </div>
    </div>
  )
}
